import * as React from 'react'

import { TextInputFieldV2 as TextInputField } from 'components/form'
import InputSuggestions from 'components/InputSuggestions'

import { FormikProps } from 'formik'

export type OptionType = {
  label: string
  annotation?: string
  value: string | number
  action?: React.ReactNode
}

type Props = {
  options: Array<OptionType>
  field: { name: string; value: any }
  form: FormikProps<any>
  additionalAction: React.ReactNode
  label: string
}

export default function SelectInput({
  options,
  field,
  form,
  additionalAction,
  ...restProps
}: Props) {
  const getOptionLabel = React.useCallback(
    function getOptionLabel(value: any) {
      return options.find((option) => option.value === value)?.label
    },
    [options],
  )

  const [filter, setFilter] = React.useState(
    () => getOptionLabel(field.value) || '',
  )
  const [isFilterInputFocused, setFilterInputFocused] = React.useState(false)
  const [showSuggestions, setShowSuggestions] = React.useState(false)

  const fieldWithHandlers = {
    ...field,
    onChange: handleFilterChange,
    onBlur: handleBlur,
  }

  const meta = {
    value: field.value,
    error: form.errors[field.name] as string,
    touched: form.touched[field.name] as boolean,
    initialValue: form.initialValues[field.name],
    initialTouched: false,
    initialError: undefined,
  }

  const filteredOptions =
    typeof filter === 'string'
      ? options.filter((option) =>
          option.label
            .trim()
            .toLowerCase()
            .includes(filter.trim().toLowerCase()),
        )
      : options

  React.useEffect(() => {
    if (!isFilterInputFocused) {
      setFilter(getOptionLabel(field.value) || '')
    }
  }, [field.value, isFilterInputFocused, getOptionLabel])

  function handleFocus() {
    setFilterInputFocused(true)

    if (!field.value) {
      setShowSuggestions(true)
    }
  }

  function handleBlur() {
    setFilterInputFocused(false)
    setShowSuggestions(false)

    if (filteredOptions.length === 1 && filteredOptions[0].label === filter) {
      form.setFieldValue(field.name, filteredOptions[0].value)
    }
  }

  function handleSuggestionSelect(option: OptionType) {
    form.setFieldValue(field.name, option.value)
  }

  function handleFilterChange(event: React.ChangeEvent<HTMLInputElement>) {
    const nextValue = event?.target?.value

    setFilter(nextValue)
    setShowSuggestions(true)
  }

  return (
    <div className="root">
      <TextInputField
        {...restProps}
        label={restProps.label}
        form={form}
        field={fieldWithHandlers}
        meta={meta}
        value={filter}
        forceFloat={Boolean(filter || field?.value)}
        onChange={handleFilterChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      {showSuggestions ? (
        <InputSuggestions
          suggestions={filteredOptions}
          onSelect={handleSuggestionSelect}
          additionalAction={additionalAction}
        />
      ) : null}

      <style jsx>{`
        .root {
          position: relative;
        }
      `}</style>
    </div>
  )
}
